import { memo, useCallback, useState } from 'react'
import { Controller, useFieldArray, useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'

import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import {
    type RowSelectionState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'

import { alpha } from '@mui/material/styles'
import Section from '~/components/Section'
import { useAppSelector } from '~/store'
import { getTranslationI18n } from '~/utils/i18n'
import type { GiftWithPurchaseControl } from '../hooks'
import type { GiftWithPurchaseCampaignDetail } from '../schemas'
import DialogSearchProducts, {
    type Props as DialogSearchProductsProps,
} from './DialogSearchProducts'

type Props = {
    control: GiftWithPurchaseControl
}

type CampaignProductsTableProps = Props & {
    products: GiftWithPurchaseCampaignDetail['gift_with_purchase_campaign_products']
    onRemove: (index?: number) => void
}

function CampaignProductsTable({ control, products: data, onRemove }: CampaignProductsTableProps) {
    const { t } = useTranslation('gift_with_purchase')

    const formState = useFormState({
        control,
        name: 'gift_with_purchase_campaign_products',
        exact: true,
    })
    const error = formState.errors.gift_with_purchase_campaign_products?.message

    const table = useReactTable({
        getCoreRowModel: getCoreRowModel(),
        data,
        columns: [
            {
                accessorKey: 'product',
                header: '',
                cell: ({ getValue }) => (
                    <img src={getValue()?.cover_image_url} style={{ width: 40 }} />
                ),
            },
            {
                accessorKey: 'product',
                header: t('form.campaign_products.field_product'),
                cell: ({ getValue }) => getTranslationI18n('name', getValue().translations),
            },
            {
                id: 'active',
                header: t('form.campaign_products.field_active'),
                cell: ({ row }) => (
                    <Controller
                        control={control}
                        name={`gift_with_purchase_campaign_products.${row.index}.is_active`}
                        render={({ field }) => (
                            <Switch {...field} checked={field.value ?? true} color="secondary" />
                        )}
                    />
                ),
            },
            {
                id: 'remove',
                header: t('form.campaign_products.field_remove'),
                cell: ({ row }) => (
                    <IconButton onClick={() => onRemove(row.index)}>
                        <DeleteIcon />
                    </IconButton>
                ),
            },
        ],
    })

    const rows = table.getRowModel().rows
    const hasProducts = rows.length > 0

    if (error && !hasProducts) {
        return (
            <Typography gutterBottom textAlign="center" variant="subtitle2" color="error">
                {error}
            </Typography>
        )
    }

    if (!hasProducts) {
        return null
    }

    return (
        <TableContainer sx={{ maxWidth: '50%' }}>
            <Table size="small">
                <TableHead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableCell key={header.id}>
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(function CampaignProducts({ control }: Props) {
    const { t } = useTranslation('gift_with_purchase')

    const appliesToAllProducts = useWatch({
        control,
        name: 'applies_to_all_products',
    })

    const products = useFieldArray({
        control,
        name: 'gift_with_purchase_campaign_products',
        keyName: 'keyId',
    })

    const [open, setOpen] = useState(false)
    const handleClose = useCallback(() => setOpen(false), [])

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

    function setInitialRowSelection() {
        setRowSelection(
            products.fields.reduce<RowSelectionState>((acc, { product }) => {
                acc[product.id] = true
                return acc
            }, {})
        )
    }

    const handleAddProducts: Required<DialogSearchProductsProps>['onAdd'] = useCallback(
        (selectedProducts) => {
            products.replace(
                selectedProducts.map((product) => ({
                    is_active: true,
                    product: {
                        id: product.id,
                        cover_image_url: product.cover_image_url,
                        translations: product.translations,
                    },
                }))
            )
        },
        [products]
    )

    const storeId = useWatch({
        control,
        name: 'store',
    })
    const shops = useAppSelector(
        (state) => state.initial.stores.find((s) => s.id === storeId)?.shops ?? [],
        shallowEqual
    )
    const shopPks = useFieldArray({
        control,
        name: 'shop_pks',
        keyName: 'keyId',
    })

    return (
        <Section
            sx={{ px: 0 }}
            header={<Section.Header label={t('form.campaign_products.title')} />}
        >
            <Stack gap={1} sx={{ ml: 2 }}>
                <Stack gap={3} alignItems="flex-start" sx={{ maxWidth: '50%' }}>
                    <Controller
                        name="applies_to_all_products"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                {...field}
                                checked={field.value}
                                labelPlacement="start"
                                label={t('form.campaign_info.field_applies_to_all_products')}
                                control={<Switch color="secondary" />}
                                sx={{ ml: 0 }}
                                onChange={(_, checked) => {
                                    field.onChange(checked)
                                    if (checked) {
                                        products.replace([])
                                    }
                                }}
                            />
                        )}
                    />
                    <Stack gap={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {t('form.campaign_products.campaign_shops.title')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {shops.map((shop) => {
                                const isSelected = shopPks.fields.some(({ id }) => id === shop.id)

                                return (
                                    <Chip
                                        key={shop.id}
                                        variant="filled"
                                        color={'default'}
                                        avatar={
                                            <Avatar src={shop.channel.channel_square_icon_url} />
                                        }
                                        label={shop.shop_name}
                                        sx={{
                                            backgroundColor: (theme) =>
                                                isSelected
                                                    ? alpha(theme.palette.secondary.main, 0.3)
                                                    : theme.palette.grey[100],
                                            height: 'auto',
                                            p: 1,
                                            '&:hover': {
                                                backgroundColor: (theme) =>
                                                    alpha(theme.palette.secondary.main, 0.3),
                                            },
                                        }}
                                        onClick={() => {
                                            const existingShopIndex = shopPks.fields.findIndex(
                                                ({ id }) => id === shop.id
                                            )
                                            if (existingShopIndex !== -1) {
                                                shopPks.remove(existingShopIndex)
                                            } else {
                                                shopPks.append({ id: shop.id })
                                            }
                                        }}
                                    />
                                )
                            })}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                size="small"
                                variant="text"
                                color="secondary"
                                sx={{ textTransform: 'none' }}
                                onClick={() => {
                                    shopPks.replace(shops.map((shop) => ({ id: shop.id })))
                                }}
                            >
                                {t('form.campaign_products.campaign_shops.select_all')}
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                sx={{ textTransform: 'none' }}
                                color="secondary"
                                onClick={() => {
                                    shopPks.replace([])
                                }}
                            >
                                {t('form.campaign_products.campaign_shops.deselect_all')}
                            </Button>
                        </Box>
                    </Stack>
                    {!appliesToAllProducts && (
                        <Box>
                            <Button
                                startIcon={<AddIcon />}
                                variant="outlined"
                                onClick={() => {
                                    setOpen(true)
                                    setInitialRowSelection()
                                }}
                            >
                                {t('form.campaign_products.add_product')}
                            </Button>
                        </Box>
                    )}
                </Stack>

                {appliesToAllProducts ? (
                    <Typography
                        gutterBottom
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                    >
                        {t('form.campaign_products.all_products_included')}
                    </Typography>
                ) : products.fields.length === 0 ? (
                    <Typography
                        gutterBottom
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                    >
                        {t('form.campaign_products.no_products_selected')}
                    </Typography>
                ) : (
                    <CampaignProductsTable
                        control={control}
                        products={products.fields}
                        onRemove={products.remove}
                    />
                )}
            </Stack>

            <DialogSearchProducts
                open={open}
                selectedProducts={rowSelection}
                onClose={handleClose}
                onAdd={handleAddProducts}
                onSelectedProductsChange={setRowSelection}
            />
        </Section>
    )
})
